import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  type DocumentData,
  type QueryDocumentSnapshot,
} from "firebase/firestore";
import { useCallback, useEffect, useRef, useState } from "react";

import { db } from "integrations/firebase/firestore";

import { auditLogEntrySchema, type AuditLogEntry } from "./schemas";

export type AuditLogState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | {
      status: "success";
      data: AuditLogEntry[];
      hasMore: boolean;
    };

const PAGE_SIZE = 50;

function parseEntries(
  documents: QueryDocumentSnapshot<DocumentData>[],
): AuditLogEntry[] {
  return documents.map((document) => {
    const result = auditLogEntrySchema.safeParse({
      ...document.data(),
      id: document.id,
    });
    if (!result.success) {
      throw new Error(`Audit log entry ${document.id} is invalid`);
    }
    return result.data;
  });
}

export function useAuditLog(): AuditLogState & {
  reload: () => void;
  loadMore: () => Promise<void>;
  loadingMore: boolean;
} {
  const [state, setState] = useState<AuditLogState>({ status: "loading" });
  const [tick, setTick] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const lastDocument = useRef<QueryDocumentSnapshot<DocumentData> | null>(null);

  useEffect(() => {
    let cancelled = false;

    getDocs(
      query(
        collection(db, "audit_log"),
        orderBy("at", "desc"),
        limit(PAGE_SIZE + 1),
      ),
    )
      .then((snapshot) => {
        if (cancelled) return;
        const page = snapshot.docs.slice(0, PAGE_SIZE);
        lastDocument.current = page.at(-1) ?? null;
        setState({
          status: "success",
          data: parseEntries(page),
          hasMore: snapshot.docs.length > PAGE_SIZE,
        });
      })
      .catch((error) => {
        if (cancelled) return;
        setState({
          status: "error",
          error:
            error instanceof Error ? error.message : "Failed to load audit log",
        });
      });

    return () => {
      cancelled = true;
    };
  }, [tick]);

  const reload = useCallback(() => {
    lastDocument.current = null;
    setState({ status: "loading" });
    setTick((value) => value + 1);
  }, []);

  const loadMore = useCallback(async () => {
    if (
      state.status !== "success" ||
      !state.hasMore ||
      !lastDocument.current ||
      loadingMore
    ) {
      return;
    }
    setLoadingMore(true);
    try {
      const snapshot = await getDocs(
        query(
          collection(db, "audit_log"),
          orderBy("at", "desc"),
          startAfter(lastDocument.current),
          limit(PAGE_SIZE + 1),
        ),
      );
      const page = snapshot.docs.slice(0, PAGE_SIZE);
      const entries = parseEntries(page);
      lastDocument.current = page.at(-1) ?? lastDocument.current;
      setState((current) =>
        current.status === "success"
          ? {
              status: "success",
              data: [...current.data, ...entries],
              hasMore: snapshot.docs.length > PAGE_SIZE,
            }
          : current,
      );
    } catch (error) {
      setState({
        status: "error",
        error:
          error instanceof Error ? error.message : "Failed to load audit log",
      });
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, state]);

  return { ...state, reload, loadMore, loadingMore };
}
